import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, Save, Camera, KeyRound } from 'lucide-react';

const MyProfile = () => {
  const { user, logout } = useAuth();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ phone: '', personal_email: '', current_address: '', emergency_contact: { name: '', phone: '', relationship: '' } });
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [changingPwd, setChangingPwd] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const fetchAll = async () => {
    try {
      const res = await api.get('/me/employee');
      setEmp(res.data);
      setForm({
        phone: res.data.phone || '',
        personal_email: res.data.personal_email || '',
        current_address: res.data.current_address || '',
        emergency_contact: res.data.emergency_contact || { name: '', phone: '', relationship: '' },
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/me/profile', form);
      toast.success('Profile updated');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwd.next !== pwd.confirm) { toast.error('Passwords do not match'); return; }
    if (pwd.next.length < 6) { toast.error('Min 6 characters'); return; }
    setChangingPwd(true);
    try {
      await api.post('/me/change-password', { current_password: pwd.current, new_password: pwd.next });
      toast.success('Password changed. Please login again.');
      setPwd({ current: '', next: '', confirm: '' });
      setTimeout(() => logout(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setChangingPwd(false); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !emp?.id) return;
    if (f.size > 5*1024*1024) { toast.error('Max 5MB'); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await api.post(`/employees/${emp.id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo updated');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!emp?.is_employee) {
    return (
      <Card><CardContent className="p-6 text-center">
        <p className="text-muted-foreground">No employee record linked to this account.</p>
        <p className="text-xs mt-2">Contact HR to link your profile.</p>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-profile-page">
      <h1 className="text-2xl font-bold">My Profile</h1>

      <Card>
        <CardContent className="p-6 flex flex-col sm:flex-row gap-6 items-start">
          <div className="relative">
            <Avatar className="h-24 w-24">
              {emp.photo_url ? <AvatarImage src={emp.photo_url} /> : null}
              <AvatarFallback className="text-2xl bg-primary text-primary-foreground">{emp.first_name?.[0]}{emp.last_name?.[0]}</AvatarFallback>
            </Avatar>
            <label className="absolute -bottom-1 -right-1 cursor-pointer">
              <input type="file" className="hidden" accept="image/*" onChange={uploadPhoto} data-testid="my-photo-input" />
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-secondary border shadow"><Camera size={14} /></span>
            </label>
          </div>
          <div className="flex-1 space-y-1">
            <h2 className="text-xl font-bold">{emp.first_name} {emp.last_name}</h2>
            <p className="text-sm text-muted-foreground">{emp.designation} · {emp.department}</p>
            <p className="text-sm text-muted-foreground">{emp.email}</p>
            <p className="text-xs text-muted-foreground">Employee Code: {emp.employee_code}</p>
            <p className="text-xs text-muted-foreground">Join Date: {emp.join_date}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Contact Information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="profile-phone" /></div>
          <div><Label>Personal Email</Label><Input type="email" value={form.personal_email} onChange={(e) => setForm({...form, personal_email: e.target.value})} data-testid="profile-personal-email" /></div>
          <div className="sm:col-span-2"><Label>Current Address</Label><Input value={form.current_address} onChange={(e) => setForm({...form, current_address: e.target.value})} data-testid="profile-address" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Emergency Contact</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label>Name</Label><Input value={form.emergency_contact.name || ''} onChange={(e) => setForm({...form, emergency_contact: {...form.emergency_contact, name: e.target.value}})} /></div>
          <div><Label>Relationship</Label><Input value={form.emergency_contact.relationship || ''} onChange={(e) => setForm({...form, emergency_contact: {...form.emergency_contact, relationship: e.target.value}})} /></div>
          <div><Label>Phone</Label><Input value={form.emergency_contact.phone || ''} onChange={(e) => setForm({...form, emergency_contact: {...form.emergency_contact, phone: e.target.value}})} /></div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-profile-btn">{saving && <Loader2 size={14} className="animate-spin mr-1" />}<Save size={16} className="mr-2" />Save Changes</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><KeyRound size={18} />Change Password</CardTitle></CardHeader>
        <CardContent className="space-y-3 max-w-md">
          <div><Label>Current Password</Label><Input type="password" value={pwd.current} onChange={(e) => setPwd({...pwd, current: e.target.value})} data-testid="pwd-current" /></div>
          <div><Label>New Password</Label><Input type="password" value={pwd.next} onChange={(e) => setPwd({...pwd, next: e.target.value})} data-testid="pwd-new" /></div>
          <div><Label>Confirm Password</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({...pwd, confirm: e.target.value})} data-testid="pwd-confirm" /></div>
          <Button onClick={changePassword} disabled={changingPwd} data-testid="change-pwd-btn">
            {changingPwd && <Loader2 size={14} className="animate-spin mr-1" />}Change Password
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyProfile;
